import { z } from "zod";
import { InventoryCategories, MeasurementUnits } from "./enums";
import { INVENTORY_ITEM_DESCRIPTION_MAX_LENGTH } from "./helpers";

export const AddKitchenSchema = z.object({
  name: z.string().min(1, "Kitchen name is required"),
  address: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  county: z.string().optional(),
  state: z.string().min(1, "State is required"),
  zipcode: z.string().min(5, "Zipcode is required"),
  bgPhoto: z.any().optional(),
  logo: z.any().optional(),
  website: z.string().url("Invalid URL").optional(),
  width: z.string().optional(),
  length: z.string().optional(),
  capacity: z.string().optional(),
  description: z.string().optional(),
});

export const AddInventorySchema = z.object({
  item_name: z.string().min(1, "Item name is required"),
  total_quantity: z.number().min(1, "Quantity must be at least 1"),
  category: z.string().min(1, "Category is required"),
  measure_unit: z.string().min(1, "Measurement unit is required"),
  unit_price: z.number().min(0, "Unit price is required"),
  is_reusable: z.boolean().optional(),
  track_usage: z.boolean().optional(),
  // usage_measure_unit: z.string().optional(),
  // usage_unit_cost: z.number().min(0, "Usage unit cost must be 0 or greater").optional(),
  // space_allocation: z.enum(["dedicated", "shared"]).optional(),
  image_url: z.any().optional(),
  item_description: z
    .string()
    .max(
      INVENTORY_ITEM_DESCRIPTION_MAX_LENGTH,
      `Description must be ${INVENTORY_ITEM_DESCRIPTION_MAX_LENGTH} characters or fewer`
    )
    .optional(),
});

const BasePlanSchema = z.object({
  plan_name: z.string().min(1, "Plan name is required"),
  plan_description: z.string().optional(),
  plan_rate: z.number().min(0, "Plan rate must be 0 or greater"),
  plan_recurrence: z.enum(["hourly", "daily", "weekly", "monthly", "annual"], {
    required_error: "Recurrence is required",
    invalid_type_error:
      "Recurrence must be one of: hourly, daily, weekly, monthly, annual",
  }),
  plan_max_hours: z.number().min(0, "Max hours is required"),
  // Optional list of human-readable plan features
  features: z.array(z.string()).optional(),

  plan_type: z.enum(["membership", "resource", "dual"]).optional(),
  is_fulltime_hours: z.boolean().optional(),
  associated_resources: z.array(z.object({
    resource_id: z.string().optional(),
    resource_name: z.string().optional(),
    max_duration: z.number().min(0, "Max duration must be 0 or greater"),
    inventory: z.array(z.object({
      inventory_id: z.string(),
      max_qty: z.number().min(1, "Max quantity must be at least 1"),
    })).optional(),
  })).optional(),

  // New fields for resource plans
  pricing_type: z.enum(["fixed", "dynamic"], {
    required_error: "Pricing type is required",
  }),
  resource_type: z.enum([
    "kitchen",
    "documentation",
    "training",
    "funding",
    "expert",
    "farm",
  ]).optional(),
  overage_rate: z.number().min(0, "Overage rate must be 0 or greater").optional(),

  // Dynamic pricing fields
  // plan_rate: z.number().min(0, "Plan rate must be 0 or greater").optional(),
  peak_rate: z.number().min(0, "Peak rate must be 0 or greater").optional(),
  off_peak_rate: z
    .number()
    .min(0, "Off-peak rate must be 0 or greater")
    .optional(),
});

export const AddPlanSchema = BasePlanSchema.superRefine((data, ctx) => {
  if (data.pricing_type === "dynamic") {
    // Require dynamic rate fields
    if (data.plan_rate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["plan_rate"],
        message: "Standard rate is required for dynamic pricing",
      });
    }
    if (data.peak_rate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["peak_rate"],
        message: "Peak rate is required for dynamic pricing",
      });
    }
    if (data.off_peak_rate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["off_peak_rate"],
        message: "Off-peak rate is required for dynamic pricing",
      });
    }
  } 
  // else if (data.pricing_type === "fixed") {
  //   // plan_rate is the effective rate; ensure non-negative (already enforced)
  //   // Ensure standard_rate mirrors plan_rate if provided
  //   if (data.plan_rate != null && data.plan_rate !== data.plan_rate) {
  //     ctx.addIssue({
  //       code: z.ZodIssueCode.custom,
  //       path: ["plan_rate"],
  //       message: "Standard rate must equal plan rate for fixed pricing",
  //     });
  //   }
  // }
});

export const EditPlanSchema = BasePlanSchema.extend({
  plan_id: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.pricing_type === "dynamic") {
    // Require dynamic rate fields
    if (data.plan_rate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["plan_rate"],
        message: "Standard rate is required for dynamic pricing",
      });
    }
    if (data.peak_rate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["peak_rate"],
        message: "Peak rate is required for dynamic pricing",
      });
    }
    if (data.off_peak_rate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["off_peak_rate"],
        message: "Off-peak rate is required for dynamic pricing",
      });
    }
  } 
});

export const AddFeeSchema = z.object({
  name: z.string().min(1, "Fee name is required"),
  // rate: z.number().min(0, "Fee rate is required"),
  rate: z.number().min(0.01, "Fee rate must be greater than 0"),
  description: z.string().optional(),
});


export const AddDiscountSchema = z.object({
  name: z.string().min(1, "Discount name is required"),
  rate: z.number().min(0.01, "Discount rate must be greater than 0"),
  description: z.string().optional(),
  discount_type: z.enum(["fixed", "percentage"], {
    required_error: "Discount type is required",
    invalid_type_error: "Discount type must be either 'fixed' or 'percentage'"
  }),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
}).refine((data) => {
  if (data.start_date && data.end_date) {
    return new Date(data.start_date) <= new Date(data.end_date);
  }
  return true;
}, {
  message: "End date must be after start date",
  path: ["end_date"]
});

export const AddRequirementSchema = z.object({
  name: z.string().min(1, "Requirement name is required"),
  validity: z.string().optional(),
  is_orientation_training: z.boolean().optional(),
});

export const AddCredentialSchema = z.object({
  category: z.string().min(1, "Category is required"),
  file: z.any().refine((file) => file, { message: "File is required" }),
});

export const AddTrainingInventorySchema = z.object({
  item_name: z.string().min(1, "Training name is required"),
  item_description: z.string().optional(),
  category: z.string().min(1, "Category is required"),
  unit_price: z.number().min(0, "Unit price is required"),
  total_quantity: z.number().min(1, "Capacity must be at least 1"),
  measure_unit: z.string().min(1, "Measurement unit is required"),
  date: z.string().min(1, "Date is required"),
  times: z.array(z.string()).min(1, "At least one time is required"),
  duration: z.number().min(1, "Duration must be at least 1 hour"),
});

export const AddFarmSchema = z.object({
  name: z.string().min(1, 'Farm name is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipcode: z.string().min(5, 'Zipcode is required'),
  description: z.string().min(1, 'Description is required'),
  bgPhoto: z.any().optional(),
});

export const AddTrainingResourceSchema = z.object({
  name: z.string().min(1, 'Training name is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipcode: z.string().min(5, 'Zipcode is required'),
  description: z.string().min(1, 'Description is required'),
  total_amount: z.number().min(0, 'Training cost must be 0 or greater').optional(),
  bgPhoto: z.any().optional(),
});

const fundingSharedFields = {
  name: z.string().min(1, 'Funding name is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipcode: z.string().min(5, 'Zipcode is required'),
  description: z.string().min(1, 'Description is required'),
  resource_subtype: z.string().min(1, 'Funding type is required'),
  website: z.string().optional(),
  bgPhoto: z.any().optional(),
};

export const AddCapitalFundingResourceSchema = z.object({
  ...fundingSharedFields,
  funding_kind: z.literal('capital'),
  total_amount: z.number().min(0, 'Funding amount must be 0 or greater'),
  end_time: z.string().optional(),
});

export const AddFinancialIntelligenceResourceSchema = z.object({
  ...fundingSharedFields,
  funding_kind: z.literal('financial_intelligence'),
  fulfillment_mode: z.enum(['referral_handoff', 'api_handoff', 'external_trial']),
  wheels: z.array(z.string()).optional(),
  requires_active_sales: z.boolean().optional(),
  eligibility_notes: z.string().optional(),
  partner_slug: z.string().optional(),
  partner_display_name: z.string().optional(),
  signup_url: z.string().optional(),
  partner_referral_code: z.string().optional(),
  attribution_window_days: z.number().min(1).optional(),
  privacy_url: z.string().optional(),
  webhook_url: z.string().optional(),
  pricing_summary: z.string().optional(),
  trial_days: z.number().min(0).optional(),
  total_amount: z.number().min(0, 'Funding amount must be 0 or greater').optional(),
  end_time: z.string().optional(),
});

export const AddFundingResourceSchema = z.discriminatedUnion('funding_kind', [
  AddCapitalFundingResourceSchema,
  AddFinancialIntelligenceResourceSchema,
]);

export const AddFundingStep1Schema = z.object({
  funding_kind: z.enum(['capital', 'financial_intelligence']),
  ...fundingSharedFields,
  wheels: z.array(z.string()).optional(),
});

export const AddDocumentationResourceSchema = z.object({
  name: z.string().min(1, 'Documentation name is required'),
  description: z.string().min(1, 'Description is required'),
  total_amount: z.number().min(0, 'Documentation cost must be 0 or greater').optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipcode: z.string().min(5, 'Zipcode is required'),
});

export const AddExpertResourceSchema = z.object({
  name: z.string().min(1, 'Expert name is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zipcode: z.string().min(5, 'Zipcode is required'),
  description: z.string().min(1, 'Description is required'),
  resource_subtype: z.string().min(1, 'Expertise is required'),
  total_amount: z.number().min(0, 'Session cost must be 0 or greater').optional(),
  bgPhoto: z.any().optional(),
  // is_verified: z.literal(true, { errorMap: () => ({ message: 'You must verify this expert resource.' }) }),
});

// InventoryZodSchema
z.object({
  item_name: z.string().min(1),
  item_description: z.string().optional(),
  total_quantity: z.number().min(0),
  category: z.nativeEnum(InventoryCategories),
  measure_unit: z.nativeEnum(MeasurementUnits),
  unit_price: z.number().min(0).optional(),
  image_url: z.any().optional(),
});

// TrainingInventoryZodSchema
z.object({
  item_name: z.string().min(1),
  item_description: z.string().optional(),
  category: z.literal("schedule"),
  unit_price: z.number().min(0).optional(),
  total_quantity: z.number().min(0),
  measure_unit: z.literal("ea"),
  date: z.string().refine(val => /^\d{4}-\d{2}-\d{2}$/.test(val)),
  times: z.array(z.string()).nonempty(),
  duration: z.number().min(1),
});

export const SignUpEmailSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address").min(1, "Email is required"),
});

export const SignUpCommunitySchema = z.object({
  name: z.string().min(1, "Business name is required"),
  description: z.string().optional(),
  address: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zipcode: z.string().min(5, "Zipcode must be at least 5 characters"),
  website: z.string().url("Invalid URL").optional().or(z.string().optional()),
  width: z.string().optional(),
  length: z.string().optional(),
  capacity: z.string().optional(),
  logo: z.string().optional(),
  bgPhoto: z.string().optional(),
  size: z.string().optional(),
});

// Invite Team Member (Member Details step)
export const InviteTeamMemberSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  role: z.enum(["viewer", "editor", "admin", "owner"], {
    required_error: "Role is required",
    invalid_type_error: "Role must be one of: viewer, editor, admin, owner",
  }),
});